// src/lib/websocketService.ts
import { io, Socket } from 'socket.io-client';
import apiClient from './apiClient';

export interface NewChannelMessage {
  // Or whatever structure your backend sends
  id: string;
  nameChannel: string;
  phoneChannel: string;
  createdAt?: string;
}

interface SocketTokenResponse {
  success: boolean;
  token: string;
}

// IMPORTANT: User needs to confirm or provide this URL
const SOCKET_URL = import.meta.env.VITE_WEBSOCKET_URL ||
                   (window.location.protocol === 'https:' ? 'wss://' : 'ws://') +
                   window.location.host + '/ws'; // Default: same host, /ws path

let socket: Socket | null = null;

// apiClient's response interceptor already unwraps `response.data`, so the
// awaited value here IS the body, not an AxiosResponse (see apiClient.ts).
async function fetchSocketToken(): Promise<string> {
  const response = (await apiClient.get(
    '/whatsapp/socket-token',
  )) as unknown as SocketTokenResponse;
  return response.token;
}

export const initWebSocket = (/* Potential args: authToken?: string | null */): Socket | null => {
  if (socket && socket.connected) {
    console.log('WebSocket already connected.');
    return socket;
  }

  console.log(`Attempting to connect to WebSocket server at ${SOCKET_URL}`);
  socket = io(SOCKET_URL, {
    reconnectionAttempts: 5,
    reconnectionDelay: 3000,
    withCredentials: true,
    path: '/socket.io/',
    transports: ['websocket', 'polling'],
    // transports: ['websocket'] // Prefer WebSocket transport - Removed to allow default negotiation
    // Function form (not a plain object): re-fetches a fresh token on every
    // (re)connection attempt instead of a single token captured once at
    // initial connect, which would go stale (default token TTL is 60s).
    // Never resolves with a fabricated/placeholder token on failure — an
    // empty object degrades safely because ChannelGateway's
    // handleConnection already tolerates a missing/invalid token.
    auth: (cb: (data: { token?: string }) => void) => {
      fetchSocketToken()
        .then((token) => cb({ token }))
        .catch(() => cb({}));
    },
  });

  // Dev-only debug hook: lets manual QA drive the socket from the DevTools
  // console (e.g. window.__socket.emit('join_channel_room', {channelId})).
  // Never included in a prod build (import.meta.env.DEV is stripped by Vite).
  if (import.meta.env.DEV) {
    (window as any).__socket = socket;
  }

  socket.on('connect', () => {
    setTimeout(() => {
      console.log('WebSocket connected:', socket?.id);
    }, 100);
  });

  socket.on('disconnect', (reason) => {
    console.log('WebSocket disconnected:', reason);
    if (reason === 'io server disconnect') {
      // Potentially handle auth errors or other server-initiated disconnects
    }
  });

  socket.on('connect_error', (error) => {
    console.error('WebSocket connection error:', error);
  });

  socket.on('server_message', (data: { message: string }) => { // Example generic event
    console.log('Message from server (WebSocket):', data.message);
  });
  
  return socket;
};

export const getSocket = (): Socket | null => {
  if (!socket) {
    // console.warn('Socket not initialized. Call initWebSocket first.');
    // Attempt to initialize if not already done (e.g. if page was refreshed and App.tsx didn't re-mount yet)
    // This could be problematic if auth token is needed immediately.
    // Consider a robust way to ensure initWebSocket is called before getSocket if needed.
    return initWebSocket();
  }
  return socket;
};

type MessageCallback<T> = (message: T) => void;

export const subscribeToEvent = <T>(event: string, callback: MessageCallback<T>): void => {
  const currentSocket = getSocket();
  if (currentSocket) {
    currentSocket.on(event, callback);
    console.log(`Subscribed to WebSocket event: ${event}`);
  } else {
    console.warn(`Cannot subscribe to event "${event}". Socket not available or not connected.`);
  }
};

export const emitEvent = (eventName: string, data: any): void => {
  const currentSocket = getSocket();
  if (currentSocket && currentSocket.connected) {
    currentSocket.emit(eventName, data);
    console.log(`Emitted WebSocket event: ${eventName}`, data);
  } else {
    console.warn(`Cannot emit event "${eventName}". Socket not available or not connected.`);
  }
};

export const unsubscribeFromEvent = (event: string, callback?: MessageCallback<any>): void => {
  const currentSocket = getSocket();
  if (currentSocket) {
    currentSocket.off(event, callback);
    console.log(`Unsubscribed from WebSocket event: ${event}`);
  }
};

export const disconnectWebSocket = (): void => {
  if (socket && socket.connected) {
    socket.disconnect();
  }
  socket = null; 
  console.log('WebSocket explicitly disconnected and instance cleared.');
};
